import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  Progress,
  Textarea,
  useRadio,
  VisuallyHidden,
  cn,
  Spinner,
  ScrollShadow,
  ModalFooter,
  ModalHeader,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Chip,
  Tooltip,
  Select,
  SelectItem,
  Image
  // Removed Text from import
} from '@nextui-org/react'

import { Accordion, AccordionItem } from '@nextui-org/react'

import { useWorkspaceContextData } from 'src/context/workspace'
import { useSocketContextData } from 'src/context/socket'
import { useNavigate, useParams } from 'react-router-dom'
import CreatableSelect from 'react-select/creatable'
import { useSubscriptionContext } from 'src/context/subscription'
import Mixpanel from 'src/lib/mixpanel'
import { Icon } from '@iconify/react'
import { emitter } from 'src/lib/EventEmitter'
import { useDropzone } from 'react-dropzone'
import api from 'src/lib/api'
import { PowerDocsEventsName } from 'src/types/SocketEvents'
import { RefreshCcwIcon, TrashIcon } from 'lucide-react'

import toast from 'react-hot-toast'

const inActiveButtonClass = 'bg-transparent text-default-400 pl-5 pr-5'
const activeButtonClass = 'text-black-500 font-medium bg-white rounded-md  pl-5 pr-5'

// StepperIndicator removed

// ------------------ CustomRadio ------------------ //
export const CustomRadio = (props) => {
  const {
    Component,
    children,
    isSelected,
    description,
    getBaseProps,
    getWrapperProps,
    getInputProps,
    getLabelProps,
    getLabelWrapperProps,
    getControlProps,
  } = useRadio(props)

  return (
    <Component
      {...getBaseProps()}
      className={cn(
        'group inline-flex items-center hover:opacity-70 active:opacity-50 justify-between flex-row-reverse tap-highlight-transparent',
        'max-w-[300px] cursor-pointer border-2 border-default rounded-lg gap-4 p-2',
        'data-[selected=true]:border-secondary',
      )}
    >
      <VisuallyHidden>
        <input {...getInputProps()} />
      </VisuallyHidden>
      <span {...getWrapperProps()} style={isSelected ? { borderColor: '#65d0ca' } : {}}>
        <span {...getControlProps()} style={isSelected ? { backgroundColor: '#65d0ca' } : {}} />
      </span>
      <div {...getLabelWrapperProps()}>
        {children && <span {...getLabelProps()}>{children}</span>}
        {description && <span className="text-small text-foreground opacity-70">{description}</span>}
      </div>
    </Component>
  )
}

// ------------------ STEP 1: Document Type ------------------ //
function StepDocumentType({ formData, setFormData }) {
  const handleSelectDocumentType = (type: string) => {
    setFormData((prev: any) => ({ ...prev, documentType: type }))
  }

  return (
    <div className="flex flex-col gap-8 p-2">
      <h2 className="text-xl font-semibold">DOCUMENT TYPE</h2>
      <div className="grid grid-cols-4 gap-4">
        <div
          className={`flex flex-col gap-3 border-2 p-4 rounded-lg cursor-pointer ${
            formData.documentType === 'PROPOSAL' ? 'border-secondary' : 'border-default-200'
          }`}
          onClick={() => handleSelectDocumentType('PROPOSAL')}
        >
          <div className="border rounded-xl bg-default-100 border-none p-3 w-fit">
            <Icon icon="lucide:file-text" width="20" />
          </div>
          <div>
            <h3 className="text-lg font-medium">Proposal</h3>
            <p className="text-sm text-default-500">
              High-level pitch, outlining approach, value, and potential solutions.
            </p>
          </div>
          <div className="flex items-center gap-1 text-secondary uppercase text-tiny font-semibold">
            Get started <Icon icon="lucide:arrow-right" width="20" />
          </div>
        </div>
        <div
          className={`flex flex-col gap-3 border-2 p-4 rounded-lg cursor-pointer ${
            formData.documentType === 'QUOTATION' ? 'border-secondary' : 'border-default-200'
          }`}
          onClick={() => handleSelectDocumentType('QUOTATION')}
        >
          <div className="border rounded-xl bg-default-100 border-none p-3 w-fit">
            <Icon icon="lucide:file-check" width="20" />
          </div>
          <div>
            <h3 className="text-lg font-medium">Quotation</h3>
            <p className="text-sm text-default-500">
              Breakdown of costs, deliverables, and timeline, giving a financial scope.
            </p>
          </div>
          <div className="flex items-center gap-1 text-secondary uppercase text-tiny font-semibold">
            Get started <Icon icon="lucide:arrow-right" width="20" />
          </div>
        </div>
        <div
          className={`flex flex-col gap-3 border-2 p-4 rounded-lg cursor-pointer ${
            formData.documentType === 'OFFER' ? 'border-secondary' : 'border-default-200'
          }`}
          onClick={() => handleSelectDocumentType('OFFER')}
        >
          <div className="border rounded-xl bg-default-100 border-none p-3 w-fit">
            <Icon icon="lucide:file-spreadsheet" width="20" />
          </div>
          <div>
            <h3 className="text-lg font-medium">Offer</h3>
            <p className="text-sm text-default-500">
              The formal agreement to proceed with the work based on prior discussions.
            </p>
          </div>
          <div className="flex items-center gap-1 text-secondary uppercase text-tiny font-semibold">
            Get started <Icon icon="lucide:arrow-right" width="20" />
          </div>
        </div>
        <div
          className={`flex flex-col gap-3 border-2 p-4 rounded-lg cursor-not-allowed opacity-50 ${
            formData.documentType === 'CONTRACT' ? 'border-secondary' : 'border-default-200'
          }`}
        >
          <div className="flex justify-between">
            <div className="border rounded-xl bg-default-100 border-none p-3 w-fit">
              <Icon icon="lucide:file-lock" width="20" />
            </div>
            <Chip color="secondary">Coming soon</Chip>
          </div>
          <div>
            <h3 className="text-lg font-medium">Contract</h3>
            <p className="text-sm text-default-500">
              Legally binding document detailing obligations, responsibilities, and terms for all parties.
            </p>
          </div>
          <div className="flex items-center gap-1 text-secondary uppercase text-tiny font-semibold">
            Get started <Icon icon="lucide:arrow-right" width="20" />
          </div>
        </div>
      </div>
    </div>
  )
}

// ------------------ STEP 2: Project Info ------------------ //
function StepProjectInfo({
  formData,
  setFormData,
  clientsFullList,
  selectedClient,
  setSelectedClient,
  loadScope,
  errors,
  setNextButtonStatus,
}: {
  formData: any
  setFormData: (value: any) => void
  clientsFullList: any
  selectedClient: any
  setSelectedClient: (value: any) => void
  loadScope: () => void
  errors: any
  setNextButtonStatus: (value: any) => void
}) {
  const [progress, setProgress] = useState(0)
  const [variant, setVariant] = useState<'danger' | 'warning' | 'secondary'>('danger')
  const [uploading, setUpload] = useState<boolean>(false)
  const [training, setTraining] = useState<boolean>(false)
  const [reseting, setReseting] = useState<boolean>(false)
  const [filesDescription, setFilesDescription] = useState<string>('')
  const uploadDiv = useRef<HTMLDivElement>(null)
  const trainingDiv = useRef<HTMLDivElement>(null)
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setFormData((prev: any) => ({
      ...prev,
      filesToUpload: acceptedFiles,
    }))
    trainingDiv.current?.classList.add('hidden')
    uploadDiv.current?.classList.remove('hidden')
    setUpload(true)
    const formData = new FormData()
    acceptedFiles.map((file: File) => formData.append('files', file))
    const res = await api.post(`/urs/files`, formData)
    setUpload(false)
    setFormData((prev: any) => ({
      ...prev,
      filesToUpload: [],
    }))
    if (res.data?.data?.length > 0) {
      setFormData((prev: any) => ({
        ...prev,
        files: [...prev.files, ...res.data.data],
      }))
    }
  }, [])
  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    maxFiles: 5 - formData.files?.length,
  })
  const { io } = useSocketContextData()
  const resetSettings = () => {
    setFormData((prev: any) => ({
      ...prev,
      filesDescription: '',
      files: [],
      filesToUpload: [],
    }))
  }
  const trainData = async () => {
    uploadDiv.current?.classList.add('hidden')
    trainingDiv.current?.classList.remove('hidden')
    setNextButtonStatus(false)
    setTraining(true)
    const content = await io.emitWithAck('extractDescription', {
      files: formData.files,
      token: localStorage.getItem('accessToken'),
    })
    setFilesDescription(content)
    setFormData((prev: any) => ({
      ...prev,
      filesDescription: content,
      files: formData.files,
    }))
    setTraining(false)
    setNextButtonStatus(true)
    loadScope?.(formData.description + '\n' + content)
  }
  useEffect(() => {
    const words = formData?.description?.split(' ').length - 1 || 0
    const newProgress = Math.min((words / 20) * 100, 100)
    setProgress(newProgress)
    if (newProgress < 50) setVariant('danger')
    else if (newProgress < 90) setVariant('warning')
    else setVariant('secondary')
  }, [formData?.description])

  return (
    <div className="flex flex-col gap-8 p-2">
      <h2 className="text-xl font-semibold">PROJECT BRIEF</h2>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-1">
            <label className="text-sm pb-1 block">Project title</label>
            <Input
              isRequired
              variant="bordered"
              placeholder="e.g. Redesign of Running App 'Explorun'"
              classNames={{ label: 'text-secondary', inputWrapper: 'h-[55px]' }}
              value={formData?.title ?? ''}
              onChange={(e) => setFormData((prev: any) => ({ ...prev, title: e.target.value }))}
              isInvalid={errors.title && formData?.title?.length === 0}
            />
            {errors.title && formData?.title?.length === 0 && (
              <span className="text-red-500 text-xs">Project title is required</span>
            )}
          </div>
          <div className="flex-1">
            <label className="text-sm pb-1 block">Client name</label>
            <CreatableSelect
              options={clientsFullList.map((item: any) => ({
                value: item.uuid,
                label: item.name,
              }))}
              placeholder="Select or create new"
              value={selectedClient}
              onChange={(value) => {
                setFormData((prev: any) => ({
                  ...prev,
                  clientName: value?.label,
                }))
                setSelectedClient(value)
              }}
              onCreateOption={(inputValue: string) => {
                setFormData((prev: any) => ({
                  ...prev,
                  clientName: inputValue,
                }))
                setSelectedClient({ label: inputValue, value: null })
              }}
              formatCreateLabel={(inputValue: string) => (
                <div className="flex justify-between items-center">
                  New client: {inputValue}
                  <span className="z-0 group relative inline-flex items-center justify-center box-border appearance-none select-none whitespace-nowrap font-normal subpixel-antialiased overflow-hidden tap-highlight-transparent data-[pressed=true]:scale-[0.97] outline-none data-[focus-visible=true]:z-10 data-[focus-visible=true]:outline-2 data-[focus-visible=true]:outline-focus data-[focus-visible=true]:outline-offset-2 px-4 min-w-20 h-10 text-small gap-2 rounded-medium [&>svg]:max-w-[theme(spacing.8)] transition-transform-colors-opacity motion-reduce:transition-none bg-secondary text-secondary-foreground data-[hover=true]:opacity-hover">
                    <Icon icon="lucide:plus" />
                    Create client
                  </span>
                </div>
              )}
              menuPosition="fixed"
              menuPortalTarget={document.body}
              styles={{
                container: (baseStyles) => ({
                  ...baseStyles,
                  minWidth: '100%',
                  borderColor: errors.clientName ? '#f31260' : '#e5e7eb',
                  borderWidth: 2,
                  borderRadius: 8,
                  padding: 4,
                  height: '55px',
                  boxShadow: 'none',
                }),
                control: (baseStyles) => ({
                  ...baseStyles,
                  border: 'none',
                  height: '100%',
                  width: '100%',
                  outline: 'none',
                  boxShadow: 'none',
                  cursor: 'pointer',
                }),
                menuPortal: (defaultStyles) => ({
                  ...defaultStyles,
                  zIndex: 99999,
                }),
                option: (styles, state) => ({
                  ...styles,
                  cursor: 'pointer',
                }),
              }}
            />
            {errors.clientName && formData.clientName?.length === 0 && (
              <span className="text-red-500 text-xs">Client name is required</span>
            )}
          </div>
        </div>
        <div className="flex flex-col">
          <Textarea
            minRows={4}
            maxRows={6}
            variant="bordered"
            labelPlacement="outside"
            label="Project description & special requirements"
            placeholder="Provide a concise client brief for this document, including key objectives, requirements, and any specific details to consider."
            value={formData?.description ?? ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
            onBlur={(e) => loadScope(e.target.value + '\n' + filesDescription)}
            isInvalid={errors.description}
          />
          {errors.description && <span className="text-red-500 text-xs">Project description is required</span>}
          <Progress color={variant} aria-label="Prompt progress" value={progress} className="w-full mt-2" />
          {progress > 0 && (
            <span className={`text-sm text-${variant}`}>
              {progress < 50 ? 'Weak brief!' : progress < 90 ? 'Keep going!' : 'Good brief!'}
            </span>
          )}
        </div>
        <div>
          <span className={'flex flex-row text-sm items-center gap-2'}>
            Client Briefing{' '}
            <Tooltip
              className="w-[200px] px-1.5 text-tiny text-default-600 rounded-small"
              content="Enter your custom text here to fine-tune the AI agent’s responses. Provide examples or specific instructions for how you’d like the agent to behave or respond."
            >
              <Icon icon={'lucide:circle-help'} />
            </Tooltip>
          </span>
          <span className={'text-xs text-default-400'}>
            {'Upload up to 5 past documents, and our AI will analyze them in order to better generate your document.'}
          </span>
          <div
            {...getRootProps({
              className: 'flex flex-col gap-2 mt-2 bg-default-100 rounded-xl justify-center items-center',
            })}
          >
            <input {...getInputProps()} />
            {[...formData.files, ...formData.filesToUpload].length > 0 ? (
              <div className={'flex flex-col justify-center items-center gap-4 p-4 w-full'}>
                <div className={'grid grid-cols-2 gap-4 w-full'}>
                  {[...formData.files, ...formData.filesToUpload].map((file: File | { fileName: string }) => {
                    return (
                      <div
                        className={'bg-white flex rounded flex-row p-2'}
                        key={`file_${(file as File).name ?? (file as any).fileName}`}
                      >
                        <img src={'/assets/icons/file-pdf.png'} width={40} height={50} />
                        <div className={''}>
                          <p className={'text-xs'}>{(file as File).name ?? (file as any).fileName}</p>
                          <p className={'text-xs'}>PDF File</p>
                        </div>
                      </div>
                    )
                  })}
                </div>
                <div className={'flex w-full justify-center items-center gap-1 hidden'} ref={uploadDiv}>
                  <div className={'flex flex-1 h-[1px] bg-black opacity-20'} />
                  <div
                    className={
                      'pt-1 pb-1 pr-6 pl-6 rounded-2xl top-[-4px] bg-warning-400 items-center justify-center flex gap-2 border-2 border-white'
                    }
                  >
                    {uploading && <Spinner size={'sm'} color={'white'} />}
                    <span className={'text-xs text-white'}>{uploading ? 'Uploading files' : 'Document Uploaded'}</span>
                  </div>
                  <div className={'flex flex-1 h-[1px] bg-black opacity-20'} />
                </div>
                <div className={'flex w-full justify-center items-center gap-1 hidden'} ref={uploadDiv}>
                  <div className={'flex flex-1 h-[1px] bg-black opacity-20'} />
                  <div
                    className={
                      'pt-1 pb-1 pr-6 pl-6 rounded-2xl top-[-4px] bg-warning-400 items-center justify-center flex gap-2 border-2 border-white'
                    }
                  >
                    {uploadDiv && <Spinner size={'sm'} color={'white'} />}
                    <span className={'text-xs text-white'}>{uploading ? 'Uploading' : 'Document Uploaded'}</span>
                  </div>
                  <div className={'flex flex-1 h-[1px] bg-black opacity-20'} />
                </div>
                <div className={'flex w-full justify-center items-center gap-1 hidden'} ref={trainingDiv}>
                  <div className={'flex flex-1 h-[1px] bg-black opacity-20'} />
                  <div
                    className={
                      'pt-1 pb-1 pr-6 pl-6 rounded-2xl top-[-4px] bg-warning-400 items-center justify-center flex gap-2 border-2 border-white'
                    }
                  >
                    {training && <Spinner size={'sm'} color={'white'} />}
                    <span className={'text-xs text-white'}>
                      {training ? 'Processing your files' : 'File processed'}
                    </span>
                  </div>
                  <div className={'flex flex-1 h-[1px] bg-black opacity-20'} />
                </div>
                <div className={'flex flex-row flex-1 justify-between items-center p-4 w-full pt-0'}>
                  <Button
                    variant={'bordered'}
                    color={'default'}
                    style={{ borderColor: '#1c1c1c' }}
                    isDisabled={uploading || formData.files?.length === 5 || reseting}
                    onClick={() => {
                      open()
                    }}
                  >
                    <Icon icon={'lucide:plus'} color={'#1c1c1c'} />
                    <span>Upload file</span>
                  </Button>
                  <div className={'flex flex-row gap-4'}>
                    <Button
                      variant={'bordered'}
                      color={'default'}
                      style={{ borderColor: '#1c1c1c' }}
                      isDisabled={uploading || reseting}
                      onClick={resetSettings}
                    >
                      {reseting && <Spinner size={'sm'} />}
                      <span>{reseting ? 'Resetting' : 'Remove all Files'}</span>
                    </Button>
                    <Button
                      variant={'solid'}
                      color={'primary'}
                      style={{ borderColor: '#1c1c1c' }}
                      isDisabled={uploading || reseting || training}
                      onClick={trainData}
                    >
                      <span>Scan & Process now</span>
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className={'flex flex-col justify-center items-center p-4'}>
                <div className={'bg-white p-2 rounded-full mb-2'}>
                  <Icon icon={'lucide:cloud-upload'} />
                </div>
                <p className={'font-regular text-sm'}>
                  <span className={'pr-1 font-medium text-blue-400 cursor-pointer'} onClick={open}>
                    Click to upload or drag and drop
                  </span>
                  <span className={'font-light text-blue-400 '}>(max 5 files)</span>
                </p>
                <span className={'text-xs font-regular'}>PDF or DOCX (max. 5mo)</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ------------------ STEP 3: Project Scope & Generate ------------------ //
function StepProjectScope({
  formData,
  setFormData,
  scopes,
  scopesLoading,
  showAdvancedOption,
  setShowAdvancedOption,
  defaultDocLength,
  setDefaultDocLength,
  formality,
  setFormality,
  modeOfAddress,
  setModeOfAddress,
  audianceType,
  setAudianceType,
  language,
  setLanguage,
  fineTuneText,
  setFineTuneText,
  loading,
  setIsAdvancedSettingsOpen,
}) {
  return (
    <div className="flex flex-col gap-6 p-2">
      <h2 className="text-xl font-semibold">PROJECT DETAILS</h2>
      {(scopesLoading || scopes.length > 0) && (
        <div className="flex flex-col">
          <p className="pb-4 text-small leading-5 text-default-500">
            Select any relevant Scope of Work elements that apply to the project. These suggestions are optional but can
            help refine the business document by providing our AI with more context.
          </p>
          {scopesLoading && (
            <div className="w-full flex justify-center gap-2">
              <Spinner size="sm" />
              <span className="font-light opacity-40 text-default-500 text-sm">Loading scope of work options...</span>
            </div>
          )}
          {!scopesLoading && scopes.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {scopes.map((scope: string) => (
                <Button
                  key={scope}
                  onPress={() => {
                    const prev = [...(formData?.scopeOfProject ?? [])]
                    const index = prev.indexOf(scope)
                    if (index > -1) prev.splice(index, 1)
                    else prev.push(scope)
                    setFormData((frm: any) => ({ ...frm, scopeOfProject: prev }))
                  }}
                  className={
                    formData?.scopeOfProject?.includes(scope)
                      ? 'px-3 py-2 text-white font-medium'
                      : 'px-3 py-2 text-black font-medium'
                  }
                  variant={formData?.scopeOfProject?.includes(scope) ? 'solid' : 'bordered'}
                  color="secondary"
                  radius="sm"
                >
                  {scope}
                </Button>
              ))}
            </div>
          )}

          <div
            className="flex items-center mt-10 gap-1 cursor-pointer"
            onClick={() => setShowAdvancedOption((prev) => !prev)}
          >
            <Icon icon="lucide:settings" />
            {showAdvancedOption ? 'Hide advanced settings' : 'Show advanced settings'}
          </div>
        </div>
      )}
      {/* Advanced settings button replaces inline advanced settings */}
      {showAdvancedOption && (
        <Button
          className="w-fit"
          variant="bordered"
          onPress={() => setIsAdvancedSettingsOpen(true)}
        >
          Open Advanced Settings
        </Button>
      )}
    </div>
  )
}
// ------------------ MAIN COMPONENT: Onboarding (new flow) ------------------ //
export default function OnboardingWizard(props: any) {
  // --- New onboarding states ---
  const { workspace, setWorkspace } = useWorkspaceContextData();
  const [saving, setSaving] = useState(false)
  const [step, setStep] = useState(0)
  const [scraping, setScraping] = useState(false)
  const [biography, setBiography] = useState<string>('')
  const [productOffering, setProductOffering] = useState<string>('')
  const [contactPerson, setContactPerson] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [phone, setPhone] = useState<string>('')
  const [url, setUrl] = useState<string>('')
  const [address, setAddress] = useState<string>('')
  const [taxId, setTaxID] = useState<string>('')
  const [logo, setLogo] = useState<string>('')
  const [file, setFile] = useState<File>()
  const [companyName, setCompanyName] = useState<string>('')
  const [beautifyingBio, setBeautifyingBio] = useState(false)
  const [beautifyingOffering, setBeautifyingOffering] = useState(false)
  const { io } = useSocketContextData()

  const updateCompany = async () => {
    if (!workspace) return;
    setSaving(true);
    const formData = new FormData();
    formData.append('biography', biography);
    formData.append('productOffering', productOffering);
    formData.append('contactPerson', contactPerson);
    formData.append('email', email);
    formData.append('phone', phone);
    formData.append('url', url);
    formData.append('address', address);
    formData.append('taxId', taxId);
    formData.append('companyName', companyName);
    formData.append('fineTuneText', workspace.fineTuneText);
    formData.append('defaultDocLength', workspace.defaultDocLength);
    formData.append('audianceType', workspace.audianceType);
    formData.append('modeOfAddress', workspace.modeOfAddress);
    formData.append('formality', workspace.formality);
    if (file) formData.append('file', file);
    if (workspace.logo !== '') formData.append('logo', logo);
  
    try {
      const res = await api.post(`/workspaces/${workspace.uuid}`, formData);
      if (res.data?.uuid) {
        // setWorkspace(res.data);
        toast.success('Company profile updated!', {
          position: 'top-center',
          style: {
            borderRadius: '10px',
            background: '#333',
            color: '#fff',
          },
        });
        props.onClose?.();
      }
    } catch (err) {
      console.error(err);
      // toast.error('Failed to save company details.');
    } finally {
      setSaving(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setFile(acceptedFiles[0])
    if (acceptedFiles[0]) {
      const preview = URL.createObjectURL(acceptedFiles[0])
      setLogo(preview)
    }
  }, [])
  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png'],
    },
    maxFiles: 1,
  })
  const deleteFile = async () => {
    setLogo('')
    setFile(undefined)
  }

  // Stepper: now with intro (step 0)
  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      size="2xl"
      backdrop="blur"
      isDismissable={true}
      closeOnEscape={true}
      hideCloseButton={false}
    >
      <ModalContent>
        {step > 0 && (
          <div className="w-full flex justify-center pt-6">
            <div className="flex flex-row gap-4">
              {/* Step 1: Website */}
              <div className="flex flex-col items-center">
                <div className={`rounded-full h-8 w-8 flex items-center justify-center text-white font-bold text-sm ${step >= 1 ? 'bg-secondary' : 'bg-default-300'}`}>
                  1
                </div>
                <span className="text-xs mt-1 text-center">Company Website</span>
              </div>
              <div className={`h-1 w-8 mt-3 ${step >= 2 ? 'bg-secondary' : 'bg-default-200'}`} />
              {/* Step 2: Info */}
              <div className="flex flex-col items-center">
                <div className={`rounded-full h-8 w-8 flex items-center justify-center text-white font-bold text-sm ${step >= 2 ? 'bg-secondary' : 'bg-default-300'}`}>
                  2
                </div>
                <span className="text-xs mt-1 text-center">Company Info</span>
              </div>
              <div className={`h-1 w-8 mt-3 ${step >= 3 ? 'bg-secondary' : 'bg-default-200'}`} />
              {/* Step 3: Contact */}
              <div className="flex flex-col items-center">
                <div className={`rounded-full h-8 w-8 flex items-center justify-center text-white font-bold text-sm ${step === 3 ? 'bg-secondary' : 'bg-default-300'}`}>
                  3
                </div>
                <span className="text-xs mt-1 text-center">Company Contact</span>
              </div>
            </div>
          </div>
        )}
        <ModalBody>
          {step === 0 && (
            <div className="flex flex-col items-center justify-center gap-6 py-8 px-4">
              <Image src="/img/onboarding-wizard-intro.png" width={240} />
              <p className="uppercase text-sm font-semibold text-secondary tracking-wide">Let's get started</p>
              <h1 className="text-2xl font-bold text-center">
                Effortless Onboarding<br />for Perfect Business Documents
              </h1>
              <p className="text-default-500 text-sm text-center max-w-md">
                Our AI will analyze your company’s website to create a tailored profile, making it easy to generate professional business documents.
              </p>
              <Button color="secondary" size="lg" className="mt-2" radius="full" onPress={() => setStep(1)}>
                Start now
              </Button>
            </div>
          )}
          {step === 1 && (
            <div className="flex flex-col gap-6 items-center p-4">
              <Image src="/img/onboarding-wizard-step-1.png" width="240" />
              <h2 className="text-2xl font-bold text-center">Enter the link to your company website</h2>
              <p className="text-default-500 text-sm text-center max-w-md">
                We automatically scan your website and can then integrate all the information into your offer
              </p>
              <div className="flex gap-2 w-full max-w-md">
                <Input
                  type="text"
                  placeholder="e.g. acme.com"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  isDisabled={scraping}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      if (!io || !url || scraping) return
                      setScraping(true)
                      io.emit('getCompanyProfile', { url }, (response) => {
                        if (response?.description) setBiography(response.description)
                        if (response?.productOffering) setProductOffering(response.productOffering)
                        if (response?.name) setCompanyName(response.name)
                        setScraping(false)
                        setStep(2)
                      })
                    }
                  }}
                />
                <Button
                  color="secondary"
                  isLoading={scraping}
                  isDisabled={scraping}
                  onPress={async () => {
                    if (!io || !url || scraping) return;
                    setScraping(true)
                    io.emit('getCompanyProfile', { url }, (response) => {
                      if (response?.description) setBiography(response.description)
                      if (response?.productOffering) setProductOffering(response.productOffering)
                      if (response?.name) setCompanyName(response.name)
                      setScraping(false)
                      setStep(2)
                    })
                  }}
                >
                  Scrape data
                </Button>
              </div>
            </div>
          )}
          {step === 2 && (
            <>
              {/*
                Add derived state for Step 2 validity.
                isStep2Valid: both biography and productOffering are non-empty (after trim)
              */}
              {(() => {
                // This IIFE is for variable injection before return
                // eslint-disable-next-line
                const isStep2Valid = (biography?.trim?.() || '') !== '' && (productOffering?.trim?.() || '') !== '';
                return (
                  <div className="flex flex-col gap-10 p-4 py-8">
                    <div className="flex flex-col gap-2">
                      <label className="text-lg font-semibold">General information about your company</label>
                      <Textarea
                        isRequired
                        isInvalid={biography?.trim?.() === ''}
                        errorMessage={biography?.trim?.() === '' ? 'Company info is required' : ''}
                        placeholder="e.g. Innovative tech company specializing in software development, AI solutions, and digital transformation."
                        value={biography}
                        onChange={(e) => setBiography(e.target.value)}
                        isDisabled={beautifyingBio}
                        endContent={
                          <Button
                            size="sm"
                            color="secondary"
                            startContent={<Icon icon="lucide:sparkles" width="25" />}
                            variant="light"
                            isLoading={beautifyingBio}
                            onPress={() => {
                              if (!io || !biography) return
                              setBeautifyingBio(true)
                              io.emit('textProcess', {
                                selectedText: biography,
                                content: biography,
                                token: localStorage.getItem('accessToken'),
                                operation: 'grammarCheck'
                              }, (res) => {
                                if (res?.text) setBiography(res.text)
                                setBeautifyingBio(false)
                              })
                            }}
                          >
                            Beautify
                          </Button>
                        }
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-lg font-semibold">Enter a brief description of what your company provides</label>
                      <Textarea
                        isRequired
                        isInvalid={productOffering?.trim?.() === ''}
                        errorMessage={productOffering?.trim?.() === '' ? 'Product offering is required' : ''}
                        placeholder="e.g. programming, machinery, equipment, UX/UI design…"
                        value={productOffering}
                        onChange={(e) => setProductOffering(e.target.value)}
                        isDisabled={beautifyingOffering}
                        endContent={
                          <Button
                            size="sm"
                            color="secondary"
                            startContent={<Icon icon="lucide:sparkles" width="25" />}
                            variant="light"
                            isLoading={beautifyingOffering}
                            onPress={() => {
                              if (!io || !productOffering) return
                              setBeautifyingOffering(true)
                              io.emit('textProcess', {
                                selectedText: productOffering,
                                content: productOffering,
                                token: localStorage.getItem('accessToken'),
                                operation: 'grammarCheck'
                              }, (res) => {
                                if (res?.text) setProductOffering(res.text)
                                setBeautifyingOffering(false)
                              })
                            }}
                          >
                            Beautify
                          </Button>
                        }
                      />
                    </div>
                  </div>
                );
              })()}
            </>
          )}
          {step === 3 && (
            <div className="flex flex-col gap-6 p-4">
              <div>
                <span className={'flex flex-row items-center gap-1 text-small'}>
                  <span className="font-medium">Logo</span> of your Company <Tooltip content="Upload your company logo" color="foreground"><Icon icon={'lucide:circle-help'} /></Tooltip>
                </span>
                <div
                  {...getRootProps({
                    className: 'flex flex-col gap-2 mt-2 bg-default-100 rounded-xl justify-center items-center',
                  })}
                >
                  <input {...getInputProps()} />
                  <div className={'flex flex-col justify-center items-center p-4'}>
                    {logo ? (
                      <>
                        <img src={logo} className={'w-[120px] mb-5'} />
                        <div className={'flex gap-2'}>
                          <Button variant="solid" color={'secondary'} className={''} onPress={open}>
                            <RefreshCcwIcon />
                            Select file
                          </Button>
                          <Button variant="bordered" color={'danger'} className={''} onPress={deleteFile}>
                            <TrashIcon />
                            Delete File
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={'bg-white p-2 rounded-full mb-2'}>
                          <Icon icon={'lucide:cloud-upload'} />
                        </div>
                        <p className={'font-regular text-sm'}>
                          <span className={'pr-1 font-medium text-blue-400 cursor-pointer'} onClick={open}>
                            Click to upload
                          </span>
                          or drag and drop
                        </p>
                        <span className={'text-xs font-regular'}>SVG, PNG, JPG or GIF (max. 800x400px)</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
                <Input
                  className="mt-2"
                  label={
                    <span className={'flex flex-row items-center gap-1'}>
                      <span className="font-medium">Name</span> of your Company <Tooltip content="Enter your company name" color="foreground"><Icon icon={'lucide:circle-help'} /></Tooltip>
                    </span>
                  }
                  labelPlacement={'outside'}
                  type="text"
                  placeholder="e.g. ACME Inc."
                  value={companyName ? companyName : ''}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
                <Input
                  className="mt-2"
                  label={
                    <span className={'flex flex-row items-center gap-1'}>
                      <span className="font-medium">Address</span> of your Company <Tooltip content="Enter the company address" color="foreground"><Icon icon={'lucide:circle-help'} /></Tooltip>
                    </span>
                  }
                  type="text"
                  labelPlacement={'outside'}
                  placeholder="e.g. 4015 Smith St"
                  value={address ? address : ''}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
                <Input
                  className="mt-2"
                  label={
                    <span className={'flex flex-row items-center gap-1'}>
                      Contact <span className="font-medium">Person</span> <Tooltip content="Enter the name of the contact person" color="foreground"><Icon icon={'lucide:circle-help'} /></Tooltip>
                    </span>
                  }
                  labelPlacement={'outside'}
                  type="text"
                  placeholder="e.g. John Doe"
                  value={contactPerson ? contactPerson : ''}
                  onChange={(e) => setContactPerson(e.target.value)}
                />
                <Input
                  className="mt-2"
                  label={
                    <span className={'flex flex-row items-center gap-1'}>
                      Contact <span className="font-medium">Email</span> <Tooltip content="Enter the email address of the contact person" color="foreground"><Icon icon={'lucide:circle-help'} /></Tooltip>
                    </span>
                  }
                  type="text"
                  labelPlacement={'outside'}
                  placeholder="e.g. john@acme.inc"
                  value={email ? email : ''}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
                <Input
                  className="mt-2"
                  label={
                    <span className={'flex flex-row items-center gap-1'}>
                      Contact <span className="font-medium">Phone</span> <Tooltip content="Enter the phone number for the contact person" color="foreground"><Icon icon={'lucide:circle-help'} /></Tooltip>
                    </span>
                  }
                  labelPlacement={'outside'}
                  type="text"
                  placeholder="e.g. +1 (231) 4567-8903"
                  value={phone ? phone : ''}
                  onChange={(e) => setPhone(e.target.value)}
                />
                <Input
                  className="mt-2"
                  label={
                    <span className={'flex flex-row items-center gap-1'}>
                      Tax ID <Tooltip content="Enter the company tax identification number" color="foreground"><Icon icon={'lucide:circle-help'} /></Tooltip>
                    </span>
                  }
                  type="text"
                  labelPlacement={'outside'}
                  placeholder="e.g. xx-xxxxxxx"
                  value={taxId ? taxId : ''}
                  onChange={(e) => setTaxID(e.target.value)}
                />
              </div>
            </div>
          )}
        </ModalBody>
        {step > 0 && (
          <ModalFooter>
            <div className="flex w-full justify-between">
              <Button variant="bordered" color="danger" onPress={props.onClose}>Cancel</Button>
              <div className="flex gap-2">
                {step === 1 && <Button color="secondary" onPress={() => setStep(2)} isDisabled={!biography && !productOffering}>Next</Button>}
                {step === 2 && (
                  <>
                    <Button variant="bordered" onPress={() => setStep(1)}>Back</Button>
                    <Button color="secondary" onPress={() => setStep(3)} isDisabled={biography?.trim?.().length === 0 || productOffering?.trim?.().length === 0}>Next</Button>
                  </>
                )}
                {step === 3 && (
                  <>
                    <Button variant="bordered" onPress={() => setStep(2)}>Back</Button>
                    <Button color="secondary" onPress={updateCompany} isLoading={saving} isDisabled={saving}>Finish</Button>
                  </>
                )}
              </div>
            </div>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  )
}
